import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbDateStruct, NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { concatMap, finalize, map, take, tap } from 'rxjs/operators';

import { Util } from '../../common/common.util';
import { TranslationKeyMap } from '../../pulse/pulse.i18n';
import { ReportsService } from '../reports.service';
import { ScatterPlotService } from '../scatterplot/scatterplot.services';
import { toCamel } from '../td-daily-huddle/td-daily-huddle.reports.component';
import { TdRawAgentDataDates } from '../td-daily-huddle/td-daily-huddle.reports.model';
import {
    Report2x2Parameters,
    Report2x2Result,
} from '../td-tbyt-selection/report_tbyt-selection.reports.model';

@Component({
    selector: 'reporttbyt',
    templateUrl: './reports_tbyt.component.html',
    host: {
        class: 'cmps-reports-2x2',
    },
})
export class ReportTbytComponent implements OnInit {
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    i18n = TranslationKeyMap;
    usersList: IOption[];
    reportId: number;
    sub: Subscription;
    roleList: IOption[] = [];
    lastDateDataModel: NgbDateStruct;
    params: Report2x2Parameters = {
        selectedUser: null,
        selBehaviour: null,
        selOutcome: null,
        selDate: null,
        selRole: null,
        inputOutcomeTarget: null,
        inputBehaviorTarget: null,
        noData: true,
        pendingData: false,
        notFound: false,
        plotGenerated: true,
        invalidDate: false,
    };

    rawDates: TdRawAgentDataDates;
    metricsBehaviours: IOption[];
    metricsOutcomes: IOption[];

    constructor(
        private readonly _reportService: ReportsService,
        private _scatterService: ScatterPlotService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.params.pendingData = true;
        forkJoin([
            this._reportService.retrieveConfigReportSelectedJson(2).pipe(
                take(1),
                map((config) => {
                    const params = config.kpiSelectionControls[0].parameters;
                    const metricsOutcomes = params.find(
                        (p) => p.name === 'metricsOutcomes',
                    ).value as number[];
                    const metricsBehaviours = params.find(
                        (p) => p.name === 'metricsBehaviours',
                    ).value as number[];
                    return { metricsOutcomes, metricsBehaviours };
                }),
                concatMap((metrics) =>
                    forkJoin([
                        this._reportService
                            .retrieveMetricNames(
                                metrics.metricsBehaviours.join(','),
                            )
                            .pipe(
                                map((ms) =>
                                    ms.map((m) => ({
                                        label: m.name,
                                        value: m.id.toString(),
                                    })),
                                ),
                                // eslint-disable-next-line no-return-assign
                                tap((m) => (this.metricsBehaviours = m)),
                            ),
                        this._reportService
                            .retrieveMetricNames(
                                metrics.metricsOutcomes.join(','),
                            )
                            .pipe(
                                map((ms) =>
                                    ms.map((m) => ({
                                        label: m.name,
                                        value: m.id.toString(),
                                    })),
                                ),
                                // eslint-disable-next-line no-return-assign
                                tap((m) => (this.metricsOutcomes = m)),
                            ),
                    ]),
                ),
            ),
            this._reportService.retrieveRawAgentDataDates().pipe(
                take(1),
                tap((rawDates) => {
                    this.rawDates = rawDates;
                    const startDate = new Date(rawDates.lastDate);
                    startDate.setDate(startDate.getDate() - 29);
                    this._scatterService.selectedDate = new Date(
                        rawDates.lastDate,
                    ).toString();
                    this._scatterService.startDate = startDate.toString();
                    this.params.selDate = Util.dateToNgbDate(
                        new Date(rawDates.lastDate),
                    );
                }),
            ),
            this._reportService.retrieveDefaultHierarchyUser().pipe(
                take(1),
                tap((user) => {
                    this.params.selectedUser = {
                        id: user.userId,
                        name: user.userName,
                    };
                }),
            ),
        ])
            .pipe(
                tap(() => {
                    this.onChanged();
                }),
                finalize(() => {
                    this.params.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getReportResult() {
        if (!this.reportId) {
            this.params.noData = true;
            return;
        }
        this.params.pendingData = true;
        this._reportService
            .retrieveGenericReportResult(this.reportId)
            .pipe(
                take(1),
                tap((result) => {
                    if (result.resultSet && result.resultSet.length > 0) {
                        const model = toCamel<Report2x2Result>(
                            JSON.parse(result.resultSet),
                        );
                        if (Array.isArray(model) && model.length) {
                            const processedData = this.processData(model);
                            if (processedData && processedData.length > 0) {
                                this._scatterService.rawData = processedData;
                                this.params.plotGenerated = true;
                                this.params.noData = false;
                                this.params.notFound = false;
                                this.tabSet.select('w-tab-report-1');
                            } else {
                                this.params.noData = true;
                                this.params.notFound = true;
                                this.params.plotGenerated = true;
                            }
                        } else {
                            this.params.noData = true;
                            this.params.notFound = true;
                            // this.plotGenerated = true;
                        }
                    }
                }),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.params.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onChanged() {
        if (this.params.selectedUser && this.params.selDate) {
            this.params.pendingData = true;
            this._reportService
                .retrieveRolesHierarchy(
                    this.params.selectedUser.id,
                    Util.dateToDate(this.params.selDate),
                )
                .pipe(
                    take(1),
                    map((roles) =>
                        roles.map((r) => ({
                            value: r.id.toString(),
                            label: r.name,
                        })),
                    ),
                    tap((roles) => {
                        this.roleList = roles;
                        if (roles.length > 0) {
                            this.params.selRole = roles[0].value;
                        }
                    }),
                    // eslint-disable-next-line no-return-assign
                    finalize(() => (this.params.pendingData = false)),
                )
                .subscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.reportId = reportId;
        this.getReportResult();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processData(data: Report2x2Result[]) {
        let resp = new Array<Report2x2Result>();
        resp = data.filter(
            (el) =>
                el.outcome &&
                el.behaviour &&
                el.outcome > 0 &&
                el.behaviour > 0,
        );
        return resp;
    }
}
